import React, { useState } from "react";
import {
  useDismissFraudSignalMutation,
  useGetFraudSignalsQuery,
  useReviewFraudSignalMutation,
} from "@/store/admin_api";
import { FraudSignal, FraudSignalSeverity, FraudSignalType } from "@/model/interface/FraudSignal";
import { apiErrorMessage } from "@/util/apiError";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FaCheck, FaTimes } from "react-icons/fa";
import { toast } from "sonner";

const STATUS_TABS = [
  { value: "OPEN", label: "Open" },
  { value: "REVIEWED", label: "Reviewed" },
  { value: "DISMISSED", label: "Dismissed" },
  { value: "ALL", label: "All" },
];

const TYPE_LABELS: Record<FraudSignalType, string> = {
  CARD_FINGERPRINT_REUSE: "Card reused across orgs",
  RADAR_ELEVATED: "Stripe Radar elevated",
  EARLY_FRAUD_WARNING: "Early fraud warning",
  DISPUTE: "Dispute",
  SELF_REFERRAL: "Self-referral",
  REFERRAL_SHARED_CARD: "Referral with a shared card",
};

const SEVERITY_VARIANTS: Record<FraudSignalSeverity, "default" | "secondary" | "destructive" | "outline"> = {
  HIGH: "destructive",
  MEDIUM: "default",
  LOW: "secondary",
};

export default function FraudSignalsPage() {
  const [statusTab, setStatusTab] = useState("OPEN");
  const { data: signals, isLoading } = useGetFraudSignalsQuery(statusTab === "ALL" ? undefined : statusTab);
  const [reviewSignal] = useReviewFraudSignalMutation();
  const [dismissSignal] = useDismissFraudSignalMutation();
  const [busyId, setBusyId] = useState<string | null>(null);

  const handleReview = async (signal: FraudSignal) => {
    setBusyId(signal.id);
    try {
      await reviewSignal(signal.id).unwrap();
      toast.success("Marked as reviewed");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update the signal"));
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (signal: FraudSignal) => {
    setBusyId(signal.id);
    try {
      await dismissSignal(signal.id).unwrap();
      toast.success("Signal dismissed");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to dismiss the signal"));
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">Risk Signals</h2>
        <p className="text-muted-foreground text-sm">
          Card-fingerprint reuse, Stripe Radar flags, early fraud warnings and disputes raised against organisations.
        </p>
      </div>

      <Tabs value={statusTab} onValueChange={setStatusTab}>
        <TabsList>
          {STATUS_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Organisation</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Severity</TableHead>
            <TableHead>Detail</TableHead>
            <TableHead>Raised</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!signals?.length && (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                No risk signals
              </TableCell>
            </TableRow>
          )}
          {signals?.map((signal) => {
            const busy = busyId === signal.id;
            return (
              <TableRow key={signal.id}>
                <TableCell className="font-medium" title={signal.organisationId}>
                  {signal.organisationName || signal.organisationId}
                </TableCell>
                <TableCell>{TYPE_LABELS[signal.type] || signal.type}</TableCell>
                <TableCell>
                  <Badge variant={SEVERITY_VARIANTS[signal.severity] || "outline"}>{signal.severity}</Badge>
                </TableCell>
                <TableCell className="max-w-md truncate" title={signal.detail}>
                  {signal.detail || "-"}
                </TableCell>
                <TableCell>
                  {signal.createdAt ? new Date(signal.createdAt).toLocaleDateString() : "-"}
                </TableCell>
                <TableCell>
                  <Badge variant={signal.status === "OPEN" ? "outline" : "secondary"}>{signal.status}</Badge>
                </TableCell>
                <TableCell className="text-right space-x-2 whitespace-nowrap">
                  {signal.status === "OPEN" && (
                    <>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => handleReview(signal)}>
                        <FaCheck className="mr-1 h-3 w-3" />
                        Reviewed
                      </Button>
                      <Button size="sm" variant="destructive" disabled={busy} onClick={() => handleDismiss(signal)}>
                        <FaTimes className="mr-1 h-3 w-3" />
                        Dismiss
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
